import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './LiveFeed.module.css';
import { getBillDate, toLocalYMD } from '../../../utils/billDates';
import { prettyTime } from '../../../utils/appointmentMessages';

// =============================================================================
// Live board for the dashboard.
// Shows TODAY only (whatever date filter is chosen), one notice at a time,
// most urgent first: late clients, in service now, starting soon, new bills,
// later today, finished, cancelled.
// =============================================================================

const ROTATE_MS = 5000;
const INACTIVE = ['Cancelled', 'No_Show'];
const LATE_AFTER_MIN = 10;   // a booking is "late" 10 min after its start
const SOON_MIN = 60;         // "starting soon" window
const RECENT_BILL_MIN = 180; // bills from the last 3 hours
const RECENT_DONE_MIN = 120; // finished in the last 2 hours

export const KIND = {
    late: { label: 'Late', tone: 'late', rank: 0 },
    now: { label: 'In service', tone: 'now', rank: 1 },
    soon: { label: 'Soon', tone: 'soon', rank: 2 },
    bill: { label: 'Bill', tone: 'bill', rank: 3 },
    later: { label: 'Later', tone: 'later', rank: 4 },
    done: { label: 'Done', tone: 'done', rank: 5 },
    cancelled: { label: 'Cancelled', tone: 'cancelled', rank: 6 },
};

const minutesBetween = (a, b) => Math.round((a - b) / 60000);

/** "in 25 min", "in 1 hr 10 min" */
const inText = (mins) => {
    if (mins <= 0) return 'now';
    if (mins < 60) return `in ${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `in ${h} hr${m ? ` ${m} min` : ''}`;
};

const agoText = (mins) => {
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const h = Math.floor(mins / 60);
    return `${h} hr${mins % 60 >= 30 ? '+' : ''} ago`;
};

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** Local Date from "YYYY-MM-DD" + "HH:mm" */
const at = (ymd, hhmm) => {
    const [y, m, d] = String(ymd || '').split('-').map(Number);
    const [hh, mm] = String(hhmm || '').split(':').map(Number);
    if (!y || !m || !d || !Number.isFinite(hh)) return null;
    return new Date(y, m - 1, d, hh, mm || 0);
};

const serviceOf = (a) => a.serviceName || a.serviceSnapshot?.name || 'Service';
const customerName = (inv) => inv?.customer?.name || inv?.customerName || 'Walk-in customer';
const billTotal = (inv) => Number(inv?.summary?.grandTotal ?? inv?.grandTotal ?? inv?.totalAmount ?? 0) || 0;

/**
 * Build the live notices from raw appointments + completed bills.
 * Returns { items, featured, stats, hasContent }.
 */
export const buildLiveFeed = (appointments = [], invoices = [], now = new Date(), { isHQ = false, storeNameOf } = {}) => {
    const today = toLocalYMD(now);
    const nowTs = now.getTime();
    const items = [];
    const stats = { booked: 0, upcoming: 0, inService: 0, late: 0, done: 0, cancelled: 0, value: 0 };
    let featured = null;

    const where = (row) => (isHQ && storeNameOf ? storeNameOf(row) : '');

    appointments.forEach((a) => {
        if (a.date !== today) return;
        const start = at(a.date, a.startTime);
        const end = at(a.date, a.endTime) || start;
        if (!start) return;

        const inactive = INACTIVE.includes(a.status);
        stats.booked += 1;
        if (!inactive) stats.value += Number(a.finalAmount ?? a.amount) || 0;

        const base = {
            id: `apt-${a._id || a.id}`,
            appointment: a,
            who: a.clientName || 'Client',
            what: [serviceOf(a), a.staffName ? `with ${a.staffName}` : '', where(a)].filter(Boolean).join(', '),
            time: `${prettyTime(a.startTime)}${a.endTime ? ` to ${prettyTime(a.endTime)}` : ''}`,
        };

        if (a.status === 'In_Service') {
            stats.inService += 1;
            const left = minutesBetween(end, nowTs);
            const item = { ...base, kind: 'now', sortAt: end.getTime(), detail: left > 0 ? `${left} min left` : `over by ${-left} min` };
            items.push(item);
            if (!featured || featured.kind !== 'now' || end < featured.endAt) featured = { ...item, endAt: end };
            return;
        }

        if (a.status === 'Completed') {
            stats.done += 1;
            const doneAt = new Date(a.updatedAt || end);
            const ago = minutesBetween(nowTs, doneAt);
            if (ago <= RECENT_DONE_MIN) items.push({ ...base, kind: 'done', sortAt: -doneAt.getTime(), detail: agoText(Math.max(0, ago)), action: 'THANK_YOU' });
            return;
        }

        if (inactive) {
            stats.cancelled += 1;
            const changed = new Date(a.updatedAt || start);
            if (minutesBetween(nowTs, changed) <= RECENT_DONE_MIN) items.push({ ...base, kind: 'cancelled', sortAt: start.getTime(), detail: a.status === 'No_Show' ? 'No show' : 'Cancelled', action: 'CANCELLED' });
            return;
        }

        // Confirmed / Waiting
        const minsToStart = minutesBetween(start, nowTs);
        if (minsToStart < -LATE_AFTER_MIN) {
            stats.late += 1;
            items.push({ ...base, kind: 'late', sortAt: start.getTime(), detail: `late by ${-minsToStart} min`, action: 'REMINDER' });
            return;
        }

        stats.upcoming += 1;
        const kind = minsToStart <= SOON_MIN ? 'soon' : 'later';
        const item = { ...base, kind, sortAt: start.getTime(), detail: minsToStart <= 0 ? 'starting now' : inText(minsToStart), action: 'REMINDER', startAt: start };
        items.push(item);
        if (!featured || (featured.kind !== 'now' && start < featured.startAt)) featured = item;
    });

    // Bills completed recently (today)
    invoices.forEach((inv) => {
        const done = getBillDate(inv);
        if (Number.isNaN(done.getTime()) || toLocalYMD(done) !== today) return;
        const ago = minutesBetween(nowTs, done);
        if (ago < 0 || ago > RECENT_BILL_MIN) return;
        items.push({
            id: `bill-${inv._id}`,
            kind: 'bill',
            invoice: inv,
            who: customerName(inv),
            what: [`Bill ${inv.invoiceNo ? `#${inv.invoiceNo}` : ''}`.trim(), money(billTotal(inv)), where(inv)].filter(Boolean).join(', '),
            time: prettyTime(`${done.getHours()}:${String(done.getMinutes()).padStart(2, '0')}`),
            detail: agoText(ago),
            sortAt: -done.getTime(),
        });
    });

    items.sort((x, y) => (KIND[x.kind].rank - KIND[y.kind].rank) || (x.sortAt - y.sortAt));

    return { items: items.slice(0, 15), featured, stats, hasContent: items.length > 0 || stats.booked > 0 };
};

/* ============================== component ============================== */
const ACTION_LABEL = { REMINDER: 'Remind', THANK_YOU: 'Thank', CANCELLED: 'Note' };
const ACTION_TITLE = { REMINDER: 'Send reminder on WhatsApp', THANK_YOU: 'Send thank-you on WhatsApp', CANCELLED: 'Send cancellation note on WhatsApp' };

export default function LiveFeed({ feed, onOpenCalendar, onWhatsApp, onOpenBill }) {
    const { items, featured, stats } = feed;
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    const count = items.length;
    const safeIndex = count ? index % count : 0;
    const current = items[safeIndex];
    const next = useCallback(() => setIndex((i) => (count ? (i + 1) % count : 0)), [count]);

    // Auto-rotate; stops on hover / focus and for reduced motion
    useEffect(() => {
        const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (paused || count < 2 || reduce) return undefined;
        const id = setInterval(next, ROTATE_MS);
        return () => clearInterval(id);
    }, [paused, count, next]);

    // Keep the same notice after a refresh, if it is still in the list
    const currentId = useRef(null);
    useEffect(() => { if (current) currentId.current = current.id; }, [current]);
    useEffect(() => {
        const i = items.findIndex((x) => x.id === currentId.current);
        if (i >= 0 && i !== safeIndex) setIndex(i);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    const kind = current ? KIND[current.kind] : null;
    const showFeatured = featured && featured.id !== current?.id;

    return (
        <section
            className={styles.panel}
            aria-label="Live today"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <header className={styles.head}>
                <span className={styles.title}><i className={styles.liveDot} aria-hidden="true" /> Live today</span>
                {onOpenCalendar && <button type="button" className={styles.link} onClick={onOpenCalendar}>Calendar</button>}
            </header>

            <p className={styles.stats}>
                <span><b>{stats.booked}</b> booked</span>
                {stats.inService > 0 && <span className={styles.sNow}><b>{stats.inService}</b> in service</span>}
                {stats.late > 0 && <span className={styles.sLate}><b>{stats.late}</b> late</span>}
                <span><b>{stats.upcoming}</b> to come</span>
                <span><b>{stats.done}</b> done</span>
            </p>

            {showFeatured && (
                <div className={`${styles.featured} ${featured.kind === 'now' ? styles.featuredNow : ''}`}>
                    <div className={styles.row}>
                        <span className={styles.featLabel}>{featured.kind === 'now' ? 'In service' : 'Up next'}</span>
                        <span className={styles.featTime}>{featured.detail}</span>
                    </div>
                    <div className={styles.row}>
                        <span className={styles.featWho}>{featured.who}</span>
                        {featured.kind !== 'now' && featured.appointment?.clientPhone && onWhatsApp && (
                            <button type="button" className={styles.action} onClick={() => onWhatsApp(featured.appointment, 'REMINDER')} title={ACTION_TITLE.REMINDER}>Remind</button>
                        )}
                    </div>
                    <span className={styles.featWhat}>{featured.time}, {featured.what}</span>
                </div>
            )}

            {count > 0 ? (
                <div className={styles.notice} aria-live="polite" aria-atomic="true">
                    <div key={current.id} className={`${styles.card} ${styles[`t_${kind.tone}`] || ''}`}>
                        <div className={styles.row}>
                            <span className={styles.kind}><i aria-hidden="true" />{kind.label}</span>
                            <span className={styles.detail}>{current.detail}</span>
                        </div>
                        <div className={styles.row}>
                            <span className={styles.who}>{current.who}</span>
                            {current.action && current.appointment?.clientPhone && onWhatsApp && (
                                <button type="button" className={styles.action} onClick={() => onWhatsApp(current.appointment, current.action)} title={ACTION_TITLE[current.action]}>
                                    {ACTION_LABEL[current.action]}
                                </button>
                            )}
                            {current.kind === 'bill' && onOpenBill && (
                                <button type="button" className={styles.action} onClick={() => onOpenBill(current.invoice)}>View</button>
                            )}
                        </div>
                        <span className={styles.what}>{current.time}, {current.what}</span>
                    </div>

                    {count > 1 && (
                        <div className={styles.pager}>
                            <div className={styles.dots} role="tablist" aria-label="Live notices">
                                {items.map((it, i) => (
                                    <button
                                        type="button"
                                        key={it.id}
                                        role="tab"
                                        aria-selected={i === safeIndex}
                                        aria-label={`${KIND[it.kind].label}: ${it.who}`}
                                        className={`${styles.dot} ${i === safeIndex ? styles.dotOn : ''} ${it.kind === 'late' ? styles.dotLate : ''}`}
                                        onClick={() => setIndex(i)}
                                    />
                                ))}
                            </div>
                            <span className={styles.counter}>{safeIndex + 1}/{count}</span>
                        </div>
                    )}
                </div>
            ) : (
                <p className={styles.quiet}>All clear for now.</p>
            )}

            {stats.value > 0 && <p className={styles.foot}>Booked today <b>{money(stats.value)}</b></p>}
        </section>
    );
}